"use client"

/** Analysis run status: Analysis Summary + Analysis Progress by lifecycle. Storybook: results shell / catalog UI. */
import { Badge, Card, CardContent, Progress, cn } from '@yeshuman/sdk'
import { ChartNoAxesColumn, CheckCircle2, Loader2 } from 'lucide-react'

import type { SearchProgressStage } from '../recruiter/search-progress'

export type SmartAnalysisRunPanelProps = {
  summary?: string | null
  stages: SearchProgressStage[]
  isSearching: boolean
  isComplete: boolean
  strategyProgress?: Record<
    string,
    { candidatesFound: number; totalProcessed?: number; isComplete?: boolean }
  >
  parallelOperations?: number
  processingSpeed?: number
}

const stageLabels: Record<string, string> = {
  parsing: 'Parsing Instructions',
  planning: 'Planning Strategies',
  searching: 'Searching',
  merging: 'Merging Results',
  analyzing: 'Analyzing Candidates',
  evaluating: 'Evaluating Satisfaction',
  refining: 'Refining Search',
  finalizing: 'Finalizing',
  complete: 'Complete',
}

const stageOrder = [
  'parsing', 'planning', 'searching', 'merging',
  'analyzing', 'evaluating', 'refining', 'finalizing', 'complete',
]

export function SmartAnalysisRunPanel({
  summary,
  stages,
  isSearching,
  isComplete,
  strategyProgress = {},
  parallelOperations,
  processingSpeed,
}: SmartAnalysisRunPanelProps) {
  const summaryText = summary?.trim() ?? ''
  const hasProgressData = isSearching || stages.length > 0 || Object.keys(strategyProgress).length > 0

  const hasContent = Boolean(summaryText) || hasProgressData
  if (!hasContent) return null

  const currentStage = stages.length > 0 ? stages[stages.length - 1]?.stage : undefined
  const progressValue = isComplete
    ? 100
    : currentStage
      ? ((stageOrder.indexOf(currentStage) + 1) / stageOrder.length) * 100
      : 0

  const visibleStages = stages.slice(-5)

  const getStepState = (index: number): 'complete' | 'active' => {
    if (isComplete) return 'complete'
    return index < visibleStages.length - 1 ? 'complete' : 'active'
  }

  return (
    <Card className="gap-0 overflow-hidden p-0">
      <div className="flex w-full items-center px-[var(--sp-card)] py-3">
        <div className="flex w-full items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <ChartNoAxesColumn className="size-4 shrink-0" aria-hidden />
            <span className="font-semibold text-sm">Analysis</span>
            {isComplete ? (
              <Badge variant="successSoft">
                <CheckCircle2 className="size-3 mr-1" aria-hidden />
                Complete
              </Badge>
            ) : isSearching ? (
              <Badge variant="secondary" className="text-xs">
                Running
              </Badge>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {isSearching && processingSpeed != null && processingSpeed > 0 ? (
              <span className="text-xs text-muted-foreground">{processingSpeed.toFixed(1)}/sec</span>
            ) : null}
            {isSearching && parallelOperations != null && parallelOperations > 0 ? (
              <Badge variant="secondary" className="text-xs">
                {parallelOperations} concurrent
              </Badge>
            ) : null}
          </div>
        </div>
      </div>

      <CardContent className="space-y-3 px-[var(--sp-card)] pb-[var(--sp-card)] pt-0">
        {hasProgressData ? <Progress value={progressValue} /> : null}

        {Object.keys(strategyProgress).length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {Object.entries(strategyProgress).map(([strategy, prog]) => (
              <div key={strategy} className="flex items-center gap-1 rounded bg-muted/30 px-2 py-1 text-xs">
                <span className="capitalize">{strategy}</span>
                <span className="text-muted-foreground">
                  {prog.candidatesFound}
                  {prog.totalProcessed != null ? ` / ${prog.totalProcessed}` : ''}
                </span>
              </div>
            ))}
          </div>
        ) : null}

        {isSearching && visibleStages.length > 0 ? (
          <div className="space-y-1">
            {visibleStages.map((stage, idx) => {
              const stepState = getStepState(idx)
              return (
                <div
                  key={idx}
                  className={cn(
                    'flex items-center gap-2 text-sm',
                    stepState === 'active' ? 'text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {stepState === 'complete' ? (
                    <CheckCircle2 className="size-4 shrink-0 text-success" aria-hidden />
                  ) : (
                    <Loader2 className="size-4 shrink-0 animate-spin text-blue-500" aria-hidden />
                  )}
                  <span>{stageLabels[stage.stage] ?? stage.stage}</span>
                  {stage.strategy ? (
                    <Badge variant="outline" className="text-xs">
                      {stage.strategy}
                    </Badge>
                  ) : null}
                  <span className="text-xs">{stage.content}</span>
                </div>
              )
            })}
          </div>
        ) : null}

        {!isSearching && summaryText ? (
          <p className="text-muted-foreground text-sm">{summaryText}</p>
        ) : null}
      </CardContent>
    </Card>
  )
}
